import { useState, useEffect, useCallback, useRef } from "react";
import { safeSetItem } from "./storage";

/**
 * useUseCaseStore — Per-use-case state store with confirmed/extracted split.
 *
 * Shape in localStorage (key: gnq_uc_state_{useCaseId}):
 * {
 *   confirmedConfig: { ... },     // User-approved values (highest priority)
 *   extractedConfig: { ... },     // From documents/LLM, pending confirmation
 *   confirmationStatus: {         // Per-section status
 *     therapy: "confirmed" | "extracted" | "empty",
 *     indication: ...,
 *     clinical_endpoints: ...,
 *     dose_config: ...,
 *     competitive_landscape: ...,
 *     commercial_assumptions: ...,
 *   },
 *   chatHistory: [],              // Single thread, all phases
 *   currentPhase: "onboarding" | "review" | "pipeline" | "complete",
 *   lastUpdated: ISO string,
 * }
 */

const SECTIONS = [
  "therapy",
  "indication",
  "clinical_endpoints",
  "dose_config",
  "dose_scenarios",
  "competitive_landscape",
  "commercial_assumptions",
];

const EMPTY_STATE = {
  confirmedConfig: {},
  extractedConfig: {},
  confirmationStatus: Object.fromEntries(SECTIONS.map((s) => [s, "empty"])),
  chatHistory: [],
  currentPhase: "onboarding",
  lastUpdated: null,
};

// Deep merge: target wins on primitives, recurse on objects, concat arrays
function deepMerge(target, source) {
  if (!source || typeof source !== "object") return target;
  if (!target || typeof target !== "object") return source;
  const result = { ...target };
  for (const key of Object.keys(source)) {
    if (
      source[key] &&
      typeof source[key] === "object" &&
      !Array.isArray(source[key])
    ) {
      result[key] = deepMerge(result[key], source[key]);
    } else if (source[key] !== undefined) {
      // Only overwrite if target doesn't have the value
      if (result[key] === undefined || result[key] === null || result[key] === "") {
        result[key] = source[key];
      }
    }
  }
  return result;
}

function loadState(useCaseId) {
  if (!useCaseId) return { ...EMPTY_STATE };
  try {
    const raw = localStorage.getItem(`gnq_uc_state_${useCaseId}`);
    if (!raw) return { ...EMPTY_STATE };
    const parsed = JSON.parse(raw);
    return { ...EMPTY_STATE, ...parsed };
  } catch {
    return { ...EMPTY_STATE };
  }
}

function saveState(useCaseId, state) {
  if (!useCaseId) return;
  // safeSetItem frees room by evicting cached report payloads rather than
  // dropping the write outright — this state carries the confirmed config.
  safeSetItem(
    `gnq_uc_state_${useCaseId}`,
    JSON.stringify({ ...state, lastUpdated: new Date().toISOString() })
  );
}

export default function useUseCaseStore(activeUseCaseId) {
  const [state, setState] = useState(() => loadState(activeUseCaseId));
  const prevIdRef = useRef(activeUseCaseId);

  // When use case changes, save current and load new
  useEffect(() => {
    if (prevIdRef.current !== activeUseCaseId) {
      // Save outgoing
      if (prevIdRef.current) saveState(prevIdRef.current, state);
      // Load incoming
      setState(loadState(activeUseCaseId));
      prevIdRef.current = activeUseCaseId;
    }
  }, [activeUseCaseId]); // eslint-disable-line

  // Persist on state change (debounced via ref to avoid excessive writes)
  const saveTimerRef = useRef(null);
  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveState(activeUseCaseId, state);
    }, 500);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [state, activeUseCaseId]);

  // ── Extracted config (from documents/LLM, pending confirmation) ──

  const extractConfig = useCallback((sectionKey, data) => {
    setState((prev) => {
      const newExtracted = { ...prev.extractedConfig };
      if (sectionKey === "auto") {
        // Merge all top-level keys from data
        Object.assign(newExtracted, data);
      } else {
        newExtracted[sectionKey] = data;
      }
      const newStatus = { ...prev.confirmationStatus };
      if (sectionKey === "auto") {
        for (const k of Object.keys(data)) {
          if (SECTIONS.includes(k) && newStatus[k] !== "confirmed") {
            newStatus[k] = "extracted";
          }
        }
      } else if (SECTIONS.includes(sectionKey) && newStatus[sectionKey] !== "confirmed") {
        newStatus[sectionKey] = "extracted";
      }
      return { ...prev, extractedConfig: newExtracted, confirmationStatus: newStatus };
    });
  }, []);

  // ── Confirm: promote extracted → confirmed ──

  const confirmSection = useCallback((sectionKey) => {
    setState((prev) => {
      const extracted = prev.extractedConfig[sectionKey];
      if (!extracted) return prev;
      const newConfirmed = { ...prev.confirmedConfig, [sectionKey]: extracted };
      const newExtracted = { ...prev.extractedConfig };
      delete newExtracted[sectionKey];
      const newStatus = { ...prev.confirmationStatus, [sectionKey]: "confirmed" };
      return { ...prev, confirmedConfig: newConfirmed, extractedConfig: newExtracted, confirmationStatus: newStatus };
    });
  }, []);

  const confirmField = useCallback((sectionKey, fieldPath, value) => {
    setState((prev) => {
      const newConfirmed = JSON.parse(JSON.stringify(prev.confirmedConfig));
      // Ensure section exists
      if (!newConfirmed[sectionKey]) newConfirmed[sectionKey] = {};
      // Set nested field
      const keys = fieldPath.split(".");
      let obj = newConfirmed[sectionKey];
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]] || typeof obj[keys[i]] !== "object") obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      const newStatus = { ...prev.confirmationStatus, [sectionKey]: "confirmed" };
      return { ...prev, confirmedConfig: newConfirmed, confirmationStatus: newStatus };
    });
  }, []);

  const confirmAll = useCallback(() => {
    setState((prev) => {
      const merged = deepMerge(prev.confirmedConfig, prev.extractedConfig);
      const newStatus = { ...prev.confirmationStatus };
      for (const k of SECTIONS) {
        if (newStatus[k] === "extracted") newStatus[k] = "confirmed";
      }
      return { ...prev, confirmedConfig: merged, extractedConfig: {}, confirmationStatus: newStatus };
    });
  }, []);

  // ── Reject: discard extracted data for a section ──

  const rejectSection = useCallback((sectionKey) => {
    setState((prev) => {
      const newExtracted = { ...prev.extractedConfig };
      delete newExtracted[sectionKey];
      const newStatus = { ...prev.confirmationStatus };
      if (newStatus[sectionKey] === "extracted") newStatus[sectionKey] = "empty";
      return { ...prev, extractedConfig: newExtracted, confirmationStatus: newStatus };
    });
  }, []);

  // ── Set confirmed directly (user manual edit, or restoring saved config) ──

  const setConfirmedConfig = useCallback((config) => {
    setState((prev) => {
      const newStatus = { ...prev.confirmationStatus };
      for (const k of SECTIONS) {
        if (config[k] !== undefined) newStatus[k] = "confirmed";
      }
      return { ...prev, confirmedConfig: config, confirmationStatus: newStatus };
    });
  }, []);

  // ── Effective config: confirmed wins, extracted fills gaps ──

  const getEffectiveConfig = useCallback(() => {
    return deepMerge(state.extractedConfig, state.confirmedConfig);
  }, [state.confirmedConfig, state.extractedConfig]);

  // ── Chat history ──

  const setChatHistory = useCallback((messagesOrFn) => {
    setState((prev) => {
      const newMessages =
        typeof messagesOrFn === "function"
          ? messagesOrFn(prev.chatHistory)
          : messagesOrFn;
      return { ...prev, chatHistory: newMessages };
    });
  }, []);

  const addChatMessage = useCallback((message) => {
    setState((prev) => ({
      ...prev,
      chatHistory: [...prev.chatHistory, message],
    }));
  }, []);

  const addPhaseDivider = useCallback((label) => {
    setState((prev) => ({
      ...prev,
      chatHistory: [
        ...prev.chatHistory,
        {
          role: "system",
          type: "divider",
          content: label,
          timestamp: new Date().toISOString(),
        },
      ],
    }));
  }, []);

  const clearChatAfterLastDivider = useCallback(() => {
    setState((prev) => {
      const lastDividerIdx = [...prev.chatHistory]
        .reverse()
        .findIndex((m) => m.type === "divider");
      if (lastDividerIdx === -1) return { ...prev, chatHistory: [] };
      const keepIdx = prev.chatHistory.length - 1 - lastDividerIdx;
      return { ...prev, chatHistory: prev.chatHistory.slice(0, keepIdx + 1) };
    });
  }, []);

  const clearAllChat = useCallback(() => {
    setState((prev) => ({ ...prev, chatHistory: [] }));
  }, []);

  // ── Phase tracking ──

  const setCurrentPhase = useCallback((phase) => {
    setState((prev) => ({ ...prev, currentPhase: phase }));
  }, []);

  // ── Reset entire use case state ──

  const resetState = useCallback(() => {
    setState({ ...EMPTY_STATE });
  }, []);

  return {
    // State
    confirmedConfig: state.confirmedConfig,
    extractedConfig: state.extractedConfig,
    confirmationStatus: state.confirmationStatus,
    chatHistory: state.chatHistory,
    currentPhase: state.currentPhase,
    effectiveConfig: getEffectiveConfig(),

    // Extraction
    extractConfig,

    // Confirmation
    confirmSection,
    confirmField,
    confirmAll,
    rejectSection,

    // Direct set
    setConfirmedConfig,

    // Effective
    getEffectiveConfig,

    // Chat
    setChatHistory,
    addChatMessage,
    addPhaseDivider,
    clearChatAfterLastDivider,
    clearAllChat,

    // Phase
    setCurrentPhase,

    // Reset
    resetState,

    // Helpers
    isConfirmed: (sectionKey) => state.confirmationStatus[sectionKey] === "confirmed",
    isExtracted: (sectionKey) => state.confirmationStatus[sectionKey] === "extracted",
    isEmpty: (sectionKey) => state.confirmationStatus[sectionKey] === "empty",
  };
}
